import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';

import { Select } from '../base/Select';
import { Button } from '../base/Button';
import { TextArea } from '../base/TextArea';
import { TextField } from '../base/TextField';
import { SectionLoading } from '../shared/SectionLoading';
import {
  IFaultDefinition,
  FaultDefinitionCategory,
  FaultDefinitionCategoryNames,
} from '../../types/Fault/FaultDefinition';
import { useGlobalHardwareModelFilter } from '../../contexts/GlobalHardwareFilterContext';
import useSuperAdminFaultDefinitionMutations from '../../data/fault/superadmin/useSuperAdminFaultDefinitionMutations';
import { GlobalHardwareModelFilterBanner } from '../shared/GlobalHardwareModelFilterBanner';
import useApplianceTypes from '../../data/appliance/useApplianceTypes';
import { ManufacturerSelect } from '../shared/ManufacturerSelect';
import { SuperAdminModelSelect } from '../shared/superadmin/SuperAdminModelSelect';
import { DataPointSelect } from '../DataPoint/DataPointSelect';
import {
  SearchableSelect,
  SearchableSelectOption,
} from '../base/SearchableSelect';
import { IFaultCause } from '../../types/Fault/FaultCause';
import { faultQueryKeys } from '../../data/QueryKeys';
import CreateEditFaultCause, {
  FaultCauseModalMode,
} from './CreateEditFaultCause';
import { FaultCauseEdit, FaultCauses } from './FaultCauses';
import { IFaultAction } from '../../types/Fault/FaultCause';
import CreateEditFaultAction, {
  FaultActionModalMode,
} from './CreateEditFaultAction';
import { FaultActionEdit, FaultActions } from './FaultActions';
import { IDocumentResource } from '../../types/Resource/DocumentResource';
import CreateEditFaultResource from './CreateEditFaultResource';
import { FaultResources } from './FaultResources';
import { faultCodeDataPoint } from '../../constants/datapointLists';
import { FormErrorsFromObject } from '../base/Form/FormErrors';

interface EditFaultDefinitionProps {
  faultDefinition?: IFaultDefinition;
}

export function CreateEditFaultDefinition({
  faultDefinition,
}: EditFaultDefinitionProps) {
  const {
    register,
    reset,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<IFaultDefinition>();
  const { applianceTypes } = useApplianceTypes();
  const { createFaultDefinition, updateFaultDefinition } =
    useSuperAdminFaultDefinitionMutations();
  const { globalHardwareModelFilter } = useGlobalHardwareModelFilter();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCause, setSelectedCause] =
    useState<SearchableSelectOption | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<FaultCauseModalMode>('create');
  const [selectedFaultCauseForModal, setSelectedFaultCauseForModal] = useState<
    IFaultCause | undefined
  >();

  // Action state
  const [selectedAction, setSelectedAction] =
    useState<SearchableSelectOption | null>(null);
  const [modalActionOpen, setModalActionOpen] = useState(false);
  const [modalActionMode, setModalActionMode] =
    useState<FaultActionModalMode>('create');
  const [selectedFaultActionForModal, setSelectedFaultActionForModal] =
    useState<IFaultAction | undefined>();

  // Resource state
  const [modalResourceOpen, setModalResourceOpen] = useState(false);
  const [editingResourceIndex, setEditingResourceIndex] = useState<
    number | null
  >(null);

  const hardwareTypeId = watch('hardwareTypeId');
  const hardwareManufacturerId = watch('hardwareManufacturerId');
  const hardwareModelId = watch('hardwareModelId');

  useEffect(() => {
    console.log('running UE');
    if (faultDefinition) {
      console.log('faultDefinition', faultDefinition);
      reset({
        ...faultDefinition,
        causes: faultDefinition.causes.length ? faultDefinition.causes : [],
        actions: faultDefinition.actions || [],
        resources: faultDefinition.resources || [],
      });
      return;
    }

    if (globalHardwareModelFilter) {
      console.log('global filter', faultDefinition);
      reset({
        hardwareTypeId: globalHardwareModelFilter.hardwareTypeId,
        hardwareManufacturerId:
          globalHardwareModelFilter.hardwareManufacturerId,
        hardwareModelId: globalHardwareModelFilter.id,
        dataPointId: faultCodeDataPoint,
        causes: [],
        actions: [],
        resources: [],
      });
      return;
    }

    console.log('no filter or fault');
    reset({ causes: [], actions: [], resources: [] });
  }, [faultDefinition, globalHardwareModelFilter, location.key, reset]);

  const handleCloneCause = () => {
    if (selectedFaultCauseForModal) {
      setModalMode('clone');
      setModalOpen(true);
    }
  };

  const handleCloneAction = () => {
    if (selectedFaultActionForModal) {
      setModalActionMode('clone');
      setModalActionOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleModalActionClose = () => {
    setModalActionOpen(false);
  };

  const handleMoveCauseUp = (index: number) => {
    const currentCauses = watch('causes') || [];
    if (index > 0) {
      const newCauses = [...currentCauses];
      [newCauses[index], newCauses[index - 1]] = [
        newCauses[index - 1],
        newCauses[index],
      ];
      setValue('causes', newCauses);
    }
  };

  const handleMoveCauseDown = (index: number) => {
    const currentCauses = watch('causes') || [];
    if (index < currentCauses.length - 1) {
      const newCauses = [...currentCauses];
      [newCauses[index], newCauses[index + 1]] = [
        newCauses[index + 1],
        newCauses[index],
      ];
      setValue('causes', newCauses);
    }
  };

  const handleRemoveCause = (index: number) => {
    const currentCauses = watch('causes') || [];
    const newCauses = currentCauses.filter((_, i) => i !== index);
    setValue('causes', newCauses);
  };

  const handleMoveActionUp = (index: number) => {
    const currentActions = watch('actions') || [];
    if (index > 0) {
      const newActions = [...currentActions];
      [newActions[index], newActions[index - 1]] = [
        newActions[index - 1],
        newActions[index],
      ];
      setValue('actions', newActions);
    }
  };

  const handleMoveActionDown = (index: number) => {
    const currentActions = watch('actions') || [];
    if (index < currentActions.length - 1) {
      const newActions = [...currentActions];
      [newActions[index], newActions[index + 1]] = [
        newActions[index + 1],
        newActions[index],
      ];
      setValue('actions', newActions);
    }
  };

  const handleRemoveAction = (index: number) => {
    const currentActions = watch('actions') || [];
    const newActions = currentActions.filter((_, i) => i !== index);
    setValue('actions', newActions);
  };

  const handleAddResource = () => {
    setEditingResourceIndex(null);
    setModalResourceOpen(true);
  };

  const handleEditResource = (index: number) => {
    setEditingResourceIndex(index);
    setModalResourceOpen(true);
  };

  const handleRemoveResource = (index: number) => {
    const currentResources = watch('resources') || [];
    const newResources = currentResources.filter((_, i) => i !== index);
    setValue('resources', newResources);
  };

  const handleResourceModalClose = (resource?: IDocumentResource) => {
    setModalResourceOpen(false);
    if (resource) {
      const currentResources = watch('resources') || [];
      if (editingResourceIndex !== null) {
        // Edit existing resource
        const newResources = [...currentResources];
        newResources[editingResourceIndex] = resource;
        setValue('resources', newResources);
      } else {
        // Add new resource
        setValue('resources', [...currentResources, resource]);
      }
    }
    setEditingResourceIndex(null);
  };

  const handleSave = (data: IFaultDefinition) => {
    if (faultDefinition) {
      updateFaultDefinition.mutate(data);
    } else {
      createFaultDefinition.mutate(data);
    }

    navigate('..');
  };

  if (!applianceTypes.data) {
    return <SectionLoading />;
  }

  return (
    <div className="space-y-6">
      <GlobalHardwareModelFilterBanner />

      <form onSubmit={handleSubmit(handleSave)} className="space-y-6">
        <FormErrorsFromObject errors={errors} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Select
            label="Hardware Type"
            {...register('hardwareTypeId', {
              required: 'Hardware Type is required',
            })}
          >
            <option disabled hidden value="">
              Please Select
            </option>
            {applianceTypes.data.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Select>

          <ManufacturerSelect
            type={hardwareTypeId}
            {...register('hardwareManufacturerId', {
              required: 'Hardware Manufacturer is required',
            })}
          />

          <SuperAdminModelSelect
            manufacturer={hardwareManufacturerId}
            type={hardwareTypeId}
            {...register('hardwareModelId', {
              required: 'Hardware Model is required',
            })}
          />

          <DataPointSelect
            type={hardwareTypeId}
            model={hardwareModelId}
            selected={watch('dataPointId')}
            {...register('dataPointId', { required: 'Data Point is required' })}
          />

          <TextField
            label="Value"
            type="number"
            {...register('value', {
              required: 'Value is required',
              valueAsNumber: true,
            })}
          />

          <Select
            label="Category"
            {...register('category', { valueAsNumber: true })}
          >
            <option disabled hidden value="">
              Please Select
            </option>
            {Object.values(FaultDefinitionCategory)
              .filter((value) => typeof value === 'number')
              .map((value) => (
                <option key={value} value={value}>
                  {
                    FaultDefinitionCategoryNames[
                      value as FaultDefinitionCategory
                    ]
                  }
                </option>
              ))}
          </Select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TextField
            className="md:col-span-1"
            label="Code"
            {...register('code')}
          />

          <div className="md:col-span-2">
            <TextField label="Title" {...register('title')} />
          </div>
        </div>
        <TextArea label="Description" {...register('description')} rows={3} />
        <div className="space-y-4">
          <span className="text-lg font-medium">Causes</span>
          <div className="flex flex-row space-x-4">
            <SearchableSelect
              url="/v1/superadmin/fault/causes"
              placeholder="Search for causes..."
              queryParam="query"
              queryKey={faultQueryKeys.superAdminCauses()}
              clearable
              containerClasses="w-1/2"
              onChange={(value) => {
                setSelectedCause(value);

                if (value && (value as any).cause) {
                  setSelectedFaultCauseForModal((value as any).cause);
                } else {
                  setSelectedFaultCauseForModal(undefined);
                }
              }}
              transformResponse={(data) =>
                data.map((item: IFaultCause) => ({
                  value: item.id,
                  label: item.title,
                  longDesc: item.description,
                  cause: item,
                }))
              }
            />
            <Button
              onClick={() =>
                setValue('causes', [
                  ...(watch('causes') || []),
                  selectedCause!.value,
                ])
              }
              disabled={!selectedCause}
            >
              Add Cause
            </Button>
            <Button onClick={handleCloneCause} disabled={!selectedCause}>
              Clone Cause
            </Button>
          </div>
          <FaultCauses
            faultCauseIds={watch('causes') || []}
            renderItem={({ causeId, index: causeIndex }, index) => (
              <FaultCauseEdit
                index={causeIndex}
                causeId={causeId}
                onMoveUp={() => handleMoveCauseUp(index)}
                onMoveDown={() => handleMoveCauseDown(index)}
                onRemove={() => handleRemoveCause(index)}
              />
            )}
          />
        </div>
        <div className="space-y-4">
          <span className="text-lg font-medium">Actions</span>
          <div className="flex flex-row space-x-4">
            <SearchableSelect
              url="/v1/superadmin/fault/actions"
              placeholder="Search for actions..."
              queryParam="query"
              queryKey={faultQueryKeys.superAdminActions()}
              clearable
              containerClasses="w-1/2"
              onChange={(value) => {
                setSelectedAction(value);
                if (value && (value as any).action) {
                  setSelectedFaultActionForModal((value as any).action);
                } else {
                  setSelectedFaultActionForModal(undefined);
                }
              }}
              transformResponse={(data) =>
                data.map((item: IFaultAction) => ({
                  value: item.id,
                  label: item.title,
                  longDesc: item.description,
                  action: item,
                }))
              }
            />
            <Button
              onClick={() =>
                setValue('actions', [
                  ...(watch('actions') || []),
                  { faultActionId: selectedAction!.value },
                ])
              }
              disabled={!selectedAction}
            >
              Add Action
            </Button>
            <Button onClick={handleCloneAction} disabled={!selectedAction}>
              Clone Action
            </Button>
          </div>
          <FaultActions
            faultActionIds={watch('actions')?.map((a) => a.faultActionId) || []}
            renderItem={({ actionId, index: actionIndex }, index) => (
              <FaultActionEdit
                index={actionIndex}
                actionId={actionId}
                onMoveUp={() => handleMoveActionUp(index)}
                onMoveDown={() => handleMoveActionDown(index)}
                onRemove={() => handleRemoveAction(index)}
              />
            )}
          />
        </div>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-lg font-medium">Resources</span>
            <Button onClick={handleAddResource}>Add Resource</Button>
          </div>
          <FaultResources
            resources={watch('resources') || []}
            showActions={true}
            onEdit={handleEditResource}
            onRemove={handleRemoveResource}
          />
        </div>{' '}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outlined"
            onClick={() => navigate('/system/fault')}
          >
            Cancel
          </Button>
          <Button type="submit">
            {faultDefinition ? 'Update' : 'Create'} Fault Definition
          </Button>
        </div>
      </form>

      <CreateEditFaultCause
        open={modalOpen}
        onClose={handleModalClose}
        faultCause={selectedFaultCauseForModal}
        onClone={(createdCause) => {
          setValue('causes', [...watch('causes'), createdCause.id]);
        }}
        mode={modalMode}
      />

      <CreateEditFaultAction
        open={modalActionOpen}
        onClose={handleModalActionClose}
        faultAction={selectedFaultActionForModal}
        onClone={(createdAction) => {
          setValue('actions', [
            ...watch('actions'),
            { faultActionId: createdAction.id },
          ]);
        }}
        mode={modalActionMode}
      />

      <CreateEditFaultResource
        open={modalResourceOpen}
        onClose={handleResourceModalClose}
        resource={
          editingResourceIndex !== null
            ? watch('resources')?.[editingResourceIndex]
            : undefined
        }
      />
    </div>
  );
}
